import React, { useMemo } from 'react'

import { ShowOne } from './ShowOne'
import type {
  ShowOneElementProps,
  ShowOneOption,
  ShowOnePanel
} from './ShowOne.type'

const parseJson = <T,>(value: string | undefined, fallback: T): T => {
  if (!value) {
    return fallback
  }
  try {
    return JSON.parse(value) as T
  } catch {
    return fallback
  }
}

const readQueryParam = (param: string): string | undefined => {
  if (typeof window === 'undefined') {
    return undefined
  }
  return new URLSearchParams(window.location.search).get(param) ?? undefined
}

// Match the query value against option `code` (uppercased, as the legacy
// component did), falling back to `key`. Returns the option key to preselect.
const resolveInitialSelection = (
  options: ShowOneOption[],
  queryValue: string | undefined
): string => {
  if (!queryValue) {
    return ''
  }
  const upper = queryValue.toUpperCase()
  const byCode = options.find(option => option.code?.toUpperCase() === upper)
  if (byCode) {
    return byCode.key
  }
  const lower = queryValue.toLowerCase()
  const byKey = options.find(option => option.key.toLowerCase() === lower)
  return byKey ? byKey.key : ''
}

/**
 * Adapter registered for `data-is="ShowOne"`. Parses the string attribute
 * contract and resolves read-only query-param preselection, then delegates to
 * the pure {@link ShowOne}.
 */
export const ShowOneElement: React.FC<ShowOneElementProps> = ({
  options,
  panels,
  panelClassName,
  queryParam,
  autoSelect,
  label,
  placeholder,
  buttonText,
  errorMessage,
  id,
  name,
  testId
}) => {
  const parsedOptions = useMemo(
    () => parseJson<ShowOneOption[]>(options, []),
    [options]
  )
  const parsedPanels = useMemo(
    () => parseJson<ShowOnePanel[]>(panels, []),
    [panels]
  )

  const initialSelection = useMemo(
    () =>
      queryParam
        ? resolveInitialSelection(parsedOptions, readQueryParam(queryParam))
        : '',
    [parsedOptions, queryParam]
  )

  return (
    <ShowOne
      options={parsedOptions}
      panels={parsedPanels}
      initialSelection={initialSelection}
      autoSelect={autoSelect === 'true'}
      panelClassName={panelClassName}
      label={label}
      placeholder={placeholder}
      buttonText={buttonText}
      errorMessage={errorMessage}
      id={id}
      name={name}
      testId={testId}
    />
  )
}
